<template>
  <div class="w-100">
    <div class="form-input" :class="[ size, { disabled: disabled, focused: focused, filled: value && String(value).length > 0, 'border-danger': errors && errors.length > 0 } ]" @click="editor?.view.dom.focus()">
      <label v-if="title && title.length" :for="randomId" class="form-input-label d-flex align-items-center justify-content-between">
        <span class="d-flex align-items-center">
          <span>{{ title }}</span> <span v-if="required" class="ms-2"><i class="fas fa-asterisk text-danger" style="font-size: 0.7em"></i></span>
        </span>
        <span class="d-flex align-items-center">
          <span v-if="max && value && value.length > 0">{{ value.length }}/{{ max }}</span>
          <button class="border-0 clickable text-primary" style="background-color: transparent; margin-top: -0.2em; margin-bottom: -0.2em" @click="editor.commands.insertContent('@')">
            <i class="fas fa-at"></i>
          </button>
        </span>
      </label>
      <div class="d-flex align-items-center justify-content-between">
        <EditorContent :editor="editor" class="form-input-body placeholderable-text-input" :id="randomId"/>
      </div>
    </div>
    <div v-if="errors && errors.length > 0" class="form-input-errors">
      <div v-for="(error, index) in errors" :key="index" class="text-danger">{{ error }}</div>
    </div>
  </div>
</template>

<script setup>
import {Editor, EditorContent} from '@tiptap/vue-3';
import Document from "@tiptap/extension-document";
import Text from "@tiptap/extension-text";
import HardBreak from "@tiptap/extension-hard-break";
import Paragraph from "@tiptap/extension-paragraph";
import History from "@tiptap/extension-history";
import {computed, ref, useSlots, watch} from "vue";
import Placeholder from "../../richtext/features/placeholder/Placeholder";


const props = defineProps({
  value: String,
  placeholders: {
    type: Array,
    required: true
  },
  max: Number,
  title: String,
  placeholder: String,
  disabled: Boolean,
  size: {
    type: String,
    default: "medium",
    validator(value) {
      return ["small", "medium", "large"].includes(value);
    }
  },
  errors: Array,
  focus: Boolean,
  required: Boolean,
  multiline: Boolean
});

const emit = defineEmits(["update:value"]);

const randomId = "input-" + Math.random().toString();

const slots = useSlots();

const reactivePlaceholders = computed(() => props.placeholders);

let editor = ref(null);
const currentValue = ref(null);
const focused = ref(false);

function transformText(text) {
  if (!text) return undefined;
  return {
    type: "doc",
    content: [
      {
        type: "paragraph",
        content: text.split(/({{[^}]*}}|\n)/gi).filter(item => item && item.length > 0).map(item => {
          if (/{{[^}]+}}/gi.test(item)) {
            return {
              type: "placeholder",
              attrs: {
                key: item.trim().replaceAll("{{", "").replaceAll("}}", "")
              }
            }
          } else {
            if (item === "\n") {
              return {
                type: "hardBreak"
              }
            } else {
              return {
                type: "text",
                text: item
              }
            }
          }
        })
      }
    ]
  };
}

let extensions = [
  Text,
  Paragraph,
  History,
  Document,
  HardBreak.extend({
    addKeyboardShortcuts () {
      return {
        Enter: () => {
          if (props.multiline) editor.value.commands.setHardBreak();
          return true
        }
      }
    }
  })
];

Placeholder.create(reactivePlaceholders).getExtensions({ slots }).forEach(extension => {
  extensions.push(extension);
});


let startValue = transformText(props.value);
currentValue.value = startValue;
editor.value = new Editor({
  content: startValue,
  extensions: extensions,
  onUpdate() {
    let text = editor.value.getJSON()?.content[0]?.content?.map(item => item.type === "text" ? item.text : item.type === "placeholder" ? "{{" + item.attrs.key + "}}" : item.type === "hardBreak" ? "\n" : "")?.join("");
    currentValue.value = text ? text : "";
    emit('update:value', text);
  },
  onFocus: () => focused.value = true,
  onBlur: () => focused.value = false,
});

watch(() => props.value, value => {
  if (value !== currentValue.value) {
    currentValue.value = value;
    editor.value.commands.setContent(transformText(value), false);
  }
});

</script>

<style>
  .placeholderable-text-input .ProseMirror, .placeholderable-text-input .ProseMirror-focused {
    outline: transparent none 0 !important;
  }
  .placeholderable-text-input .ProseMirror p {
    margin-top: 0;
    margin-bottom: 0;
  }
</style>